import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { latexToExpr, evalWithAngle, formatMathResult, getSDCycle, ANGLE, formatDecimal, formatDMS, dmsToDecimal, toEngineering, solveSimultaneous, solvePolynomial, solveInequality } from './calculator/evaluator';
import { solve, parseEquation, findVariables } from './calculator/solve';
import { CONV_CATEGORIES, convertValue } from './calculator/conversions';
import { CONSTANTS } from './calculator/constants';
import { useSEO } from './useSEO';
import './calculator/Calculator.css';

/* global MathQuill */

/* 12 ClassWiz modes (QSG p.2) */
const MODES = [
  { id: 1, name: 'Calculate',      icon: '\u2328',   shortcut: '1' },
  { id: 2, name: 'Complex',        icon: '\uD835\uDC56',    shortcut: '2' },
  { id: 3, name: 'Base-N',         icon: '\u2082\u2081\u2080', shortcut: '3' },
  { id: 4, name: 'Matrix',         icon: '\u25A6',    shortcut: '4' },
  { id: 5, name: 'Vector',         icon: '\u20D7',    shortcut: '5' },
  { id: 6, name: 'Statistics',     icon: '\uD83D\uDCCA',   shortcut: '6' },
  { id: 7, name: 'Distribution',   icon: '\u2312',   shortcut: '7' },
  { id: 8, name: 'Spreadsheet',    icon: '\u25A4',    shortcut: '8' },
  { id: 9, name: 'Table',          icon: '\u229E',   shortcut: '9' },
  { id:'A', name: 'Equation/Func', icon: 'f(x)', shortcut: 'A' },
  { id:'B', name: 'Inequality',    icon: '\u2264',    shortcut: 'B' },
  { id:'C', name: 'Ratio',         icon: 'a:b',  shortcut: 'C' },
];

/* Keyboard mapping */
const KEY_MAP = {
  '0':'0','1':'1','2':'2','3':'3','4':'4',
  '5':'5','6':'6','7':'7','8':'8','9':'9',
  '.':'decimal', '+':'add', '-':'subtract', '*':'multiply', '/':'divide',
  '(':'paren-open', ')':'paren-close',
  Enter:'equals', Backspace:'delete', Escape:'clear', Delete:'clear',
  ArrowLeft:'replay-left', ArrowRight:'replay-right',
  ArrowUp:'replay-up', ArrowDown:'replay-down',
};

/* Radical component with auto-scaling */
function Radical({ children }) {
  const rootRef = useRef(null);
  const barRef = useRef(null);
  useLayoutEffect(() => {
    const r = rootRef.current, b = barRef.current;
    if (r && b) {
      r.style.transform = 'scaleY(1)';
      const bh = b.getBoundingClientRect().height;
      const rh = r.getBoundingClientRect().height;
      if (bh > 0 && rh > 0) {
        r.style.transform = 'translateY(-0.04em) scaleY(' + (bh / rh) + ')';
      }
    }
  }, [children]);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      <span ref={rootRef} aria-hidden="true">{'\u221A'}</span>
      <span ref={barRef} style={{ borderTop: '2px solid currentColor', paddingLeft: 4, paddingRight: 4 }}>
        {children}
      </span>
    </span>
  );
}

/* Render a structured result (fraction/surd/pi) as JSX */
function ResultDisplay({ result }) {
  if (typeof result === 'string') return <span>{result}</span>;
  if (!result || !result.type) return <span>{String(result)}</span>;

  switch (result.type) {
    case 'fraction': {
      const { n, d, s } = result.value;
      return (
        <span className="res-frac">
          {s < 0 && <span className="res-sign">{'\u2212'}</span>}
          <span className="res-frac-stack">
            <span className="res-num">{n}</span>
            <span className="res-bar" />
            <span className="res-den">{d}</span>
          </span>
        </span>
      );
    }
    case 'mixed_fraction': {
      const { integer, n, d, s } = result.value;
      if (n === 0) return <span>{s < 0 ? '\u2212' : ''}{integer}</span>;
      return (
        <span className="res-frac">
          {s < 0 && <span className="res-sign">{'\u2212'}</span>}
          <span className="res-whole">{integer}</span>
          <span className="res-frac-stack">
            <span className="res-num">{n}</span>
            <span className="res-bar" />
            <span className="res-den">{d}</span>
          </span>
        </span>
      );
    }
    case 'pi_fraction': {
      const { n, d, s } = result.value;
      const numStr = n === 1 ? '\u03C0' : n + '\u03C0';
      if (d === 1) return <span>{s < 0 && '\u2212'}{numStr}</span>;
      return (
        <span className="res-frac">
          {s < 0 && <span className="res-sign">{'\u2212'}</span>}
          <span className="res-frac-stack">
            <span className="res-num">{numStr}</span>
            <span className="res-bar" />
            <span className="res-den">{d}</span>
          </span>
        </span>
      );
    }
    case 'surd': {
      const { sign, numeratorCoefficient: coeff, radicand, denominator: denom } = result.value;
      const surdPart = (
        <span>
          {coeff !== 1 && <span>{coeff}</span>}
          <Radical>{radicand}</Radical>
        </span>
      );
      if (denom === 1) return <span>{sign}{surdPart}</span>;
      return (
        <span className="res-frac">
          {sign && <span className="res-sign">{sign}</span>}
          <span className="res-frac-stack">
            <span className="res-num">{surdPart}</span>
            <span className="res-bar" />
            <span className="res-den">{denom}</span>
          </span>
        </span>
      );
    }
    case 'complex': {
      const { re, im } = result.value;
      var reStr = re !== 0 ? formatDecimal(re) : '';
      var imStr = '';
      if (im !== 0) {
        var sign = im > 0 && re !== 0 ? '+' : im < 0 ? '\u2212' : '';
        var absIm = Math.abs(im);
        imStr = sign + (absIm === 1 ? '' : formatDecimal(absIm)) + 'i';
      }
      if (!reStr && !imStr) return <span>0</span>;
      return <span>{reStr}{imStr}</span>;
    }
    default:
      return <span>{String(result)}</span>;
  }
}

/* Format a complex solution for equation mode */
function formatComplexSolution(val) {
  if (typeof val === 'number') return formatDecimal(val);
  if (val && typeof val === 'object' && 're' in val) {
    var re = Math.abs(val.re) < 1e-10 ? 0 : val.re;
    var im = Math.abs(val.im) < 1e-10 ? 0 : val.im;
    if (im === 0) return formatDecimal(re);
    var reStr = re !== 0 ? formatDecimal(re) : '';
    var sign = im > 0 && re !== 0 ? '+' : im < 0 ? '\u2212' : '';
    var absIm = Math.abs(im);
    var imStr = sign + (absIm === 1 ? '' : formatDecimal(absIm)) + 'i';
    return reStr + imStr;
  }
  return String(val);
}

/* ---- Main Calculator Component ---- */

export default function Calculator() {
  useSEO({
    title: 'Scientific calculator',
    description: 'Free in-browser ClassWiz-style scientific calculator with equation solver, statistics, matrices, vectors and unit conversions.',
    canonicalPath: '/calculator',
  });
  const [shiftMode, setShiftMode] = useState(false);
  const [alphaMode, setAlphaMode] = useState(false);
  const [hypMode, setHypMode] = useState(false);
  const [angleMode, setAngleMode] = useState(ANGLE.DEG);
  const [currentMode, setCurrentMode] = useState('Calculate');
  const [outputMode, setOutputMode] = useState('math');
  const [isPoweredOn, setIsPoweredOn] = useState(true);
  const [splashText, setSplashText] = useState(null);

  const [displayResult, setDisplayResult] = useState(null);
  const [isResultOnDisplay, setIsResultOnDisplay] = useState(false);
  const [lastAns, setLastAns] = useState(null);
  const [memory, setMemory] = useState(null);
  const [sdMode, setSdMode] = useState(0);

  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const [menuOpen, setMenuOpen] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupPage, setSetupPage] = useState('main');

  /* Variables A–F, x, y (+ M already exists) */
  const [calcVars, setCalcVars] = useState({ A: 0, B: 0, C: 0, D: 0, E: 0, F: 0, x: 0, y: 0 });
  const [preAns, setPreAns] = useState(null);

  /* SOLVE state */
  const [solveOpen, setSolveOpen] = useState(false);
  const [solveExpr, setSolveExpr] = useState('');
  const [solveVar, setSolveVar] = useState('x');
  const [solveGuess, setSolveGuess] = useState('0');
  const [solveStep, setSolveStep] = useState('expr');
  const [solveResult, setSolveResult] = useState(null);

  /* Conversion menu */
  const [convOpen, setConvOpen] = useState(false);
  const [convCategory, setConvCategory] = useState(null);

  /* Constants menu */
  const [constsOpen, setConstsOpen] = useState(false);
  const [constsPage, setConstsPage] = useState(0);

  /* Engineering notation state */
  const [engExponent, setEngExponent] = useState(null);

  /* Equation mode */
  const [eqSubMode, setEqSubMode] = useState(null);
  const [eqUnknowns, setEqUnknowns] = useState(2);
  const [eqDegree, setEqDegree] = useState(2);
  const [eqCoeffs, setEqCoeffs] = useState([]);
  const [eqEditIdx, setEqEditIdx] = useState(0);
  const [eqInput, setEqInput] = useState('');
  const [eqSolutions, setEqSolutions] = useState(null);
  const [eqSolIdx, setEqSolIdx] = useState(0);

  /* Inequality mode */
  const [ineqType, setIneqType] = useState(null);
  const [ineqDegree, setIneqDegree] = useState(2);
  const [ineqCoeffs, setIneqCoeffs] = useState([]);
  const [ineqEditIdx, setIneqEditIdx] = useState(0);
  const [ineqInput, setIneqInput] = useState('');
  const [ineqResult, setIneqResult] = useState(null);

  /* Ratio mode */
  const [ratioTemplate, setRatioTemplate] = useState(null);
  const [ratioValues, setRatioValues] = useState({ A: '', B: '', C: '', D: '' });
  const [ratioEditIdx, setRatioEditIdx] = useState(0);
  const [ratioResult, setRatioResult] = useState(null);

  /* Table mode */
  const [tableFuncs, setTableFuncs] = useState(1);
  const [tableFExpr, setTableFExpr] = useState('');
  const [tableGExpr, setTableGExpr] = useState('');
  const [tableStart, setTableStart] = useState('');
  const [tableEnd, setTableEnd] = useState('');
  const [tableStep, setTableStep] = useState('');
  const [tableData, setTableData] = useState([]);
  const [tablePhase, setTablePhase] = useState('f');
  const [tableScrollIdx, setTableScrollIdx] = useState(0);

  /* Base-N mode */
  const [baseN, setBaseN] = useState('DEC');

  /* Complex mode */
  const [complexForm, setComplexForm] = useState('rect');

  const mqRef = useRef(null);
  const mqElRef = useRef(null);
  const shellRef = useRef(null);
  const mqInitialized = useRef(false);

  /* Initialize MathQuill */
  useEffect(() => {
    if (mqInitialized.current) return;
    const el = mqElRef.current;
    if (!el || typeof MathQuill === 'undefined') return;

    const MQ = MathQuill.getInterface(2);
    const mf = MQ.MathField(el, {
      spaceBehavesLikeTab: false,
      supSubsRequireOperand: false,
      charsThatBreakOutOfSupSub: '+-=',
      autoCommands: 'pi sqrt nthRoot sum int',
      autoOperatorNames: 'sin cos tan asin acos atan sinh cosh tanh asinh acosh atanh log ln abs Ans PreAns nPr nCr Pol Rec',
    });
    mqRef.current = mf;
    mqInitialized.current = true;

    el.addEventListener('keydown', function(e) {
      e.preventDefault();
      e.stopPropagation();
    });

    var fixParens = function() {
      el.querySelectorAll('.mq-paren, .mq-lparen, .mq-rparen').forEach(function(p) {
        p.style.transform = '';
      });
    };
    var observer = new MutationObserver(fixParens);
    observer.observe(el, { childList: true, subtree: true });

    return function() { observer.disconnect(); };
  }, []);

  useEffect(function() { if (shellRef.current) shellRef.current.focus(); }, []);

  var field = function() { return mqRef.current; };

  var resultSizeClass = function() {
    if (typeof displayResult !== 'string') return 'res-normal';
    var len = displayResult.length;
    if (len > 32) return 'res-small';
    if (len > 20) return 'res-medium';
    return 'res-normal';
  };

  /* ═══════════════════════════════════════════════
     Helpers for mode-specific UIs
     ═══════════════════════════════════════════════ */

  /* Whether current mode uses custom LCD (no MathQuill) */
  var usesCustomLCD = function() {
    if (currentMode === 'Equation/Func' && eqSubMode !== null) return true;
    if (currentMode === 'Inequality' && ineqType !== null) return true;
    if (currentMode === 'Ratio' && ratioTemplate !== null) return true;
    if (currentMode === 'Table') return true;
    return false;
  };

  /* Helper: generate Table data */
  var generateTable = function() {
    try {
      var start = parseFloat(tableStart);
      var end = parseFloat(tableEnd);
      var step = parseFloat(tableStep);
      if (isNaN(start) || isNaN(end) || isNaN(step) || step === 0) return;
      if ((end - start) / step > 1000) return; // safety

      var data = [];
      var fExpr = latexToExpr(tableFExpr);
      var gExpr = tableFuncs === 2 && tableGExpr ? latexToExpr(tableGExpr) : null;

      for (var xx = start; step > 0 ? xx <= end + 1e-10 : xx >= end - 1e-10; xx += step) {
        var row = { x: xx };
        try {
          var vars = Object.assign({}, calcVars, { Ans: lastAns || 0, M: memory || 0, x: xx });
          row.fx = evalWithAngle(fExpr, angleMode, vars);
        } catch { row.fx = 'ERR'; }
        if (gExpr) {
          try {
            var vars2 = Object.assign({}, calcVars, { Ans: lastAns || 0, M: memory || 0, x: xx });
            row.gx = evalWithAngle(gExpr, angleMode, vars2);
          } catch { row.gx = 'ERR'; }
        }
        data.push(row);
      }
      setTableData(data);
      setTableScrollIdx(0);
    } catch { /* ignore */ }
  };

  /* ═══════════════════════════════════════════════
     Action handler
     ═══════════════════════════════════════════════ */
  var handleAction = useCallback(function(action) {
    var f = field();

    /* ── SOLVE overlay interaction ── */
    if (solveOpen) {
      if (action === 'on' || action === 'clear') { setSolveOpen(false); return; }
      if (solveStep === 'var') {
        var varMap = { '1': 'x', '2': 'y', '3': 'A', '4': 'B', '5': 'C', '6': 'D' };
        if (varMap[action]) { setSolveVar(varMap[action]); setSolveStep('guess'); }
        else if (action === 'equals') setSolveStep('guess');
        return;
      }
      if (solveStep === 'guess') {
        if ('0123456789'.indexOf(action) >= 0) { setSolveGuess(function(p) { return p === '0' ? action : p + action; }); return; }
        if (action === 'decimal') { setSolveGuess(function(p) { return p.indexOf('.') < 0 ? p + '.' : p; }); return; }
        if (action === 'subtract' || action === 'negate') { setSolveGuess(function(p) { return p.charAt(0) === '-' ? p.substring(1) : '-' + p; }); return; }
        if (action === 'delete') { setSolveGuess(function(p) { return p.length > 1 ? p.slice(0, -1) : '0'; }); return; }
        if (action === 'equals') {
          try {
            var allVars = Object.assign({}, calcVars, { Ans: lastAns || 0, M: memory || 0, PreAns: preAns || 0 });
            var result = solve(solveExpr, solveVar, parseFloat(solveGuess), angleMode, allVars);
            setSolveResult(result);
            setSolveStep('result');
            setLastAns(result.solution);
          } catch (e) {
            setSolveResult({ error: e.message || "Can't Solve" });
            setSolveStep('result');
          }
          return;
        }
        return;
      }
      if (solveStep === 'result') {
        setSolveOpen(false);
        return;
      }
      return;
    }

    /* ── Conversion overlay ── */
    if (convOpen) {
      if (action === 'on' || action === 'clear') { setConvOpen(false); return; }
      if (convCategory === null) {
        var catIdx = parseInt(action);
        if (catIdx >= 1 && catIdx <= CONV_CATEGORIES.length) {
          setConvCategory(CONV_CATEGORIES[catIdx - 1]);
        }
        return;
      }
      /* Pair selected */
      var pairIdx = parseInt(action);
      if (pairIdx >= 1 && convCategory && pairIdx <= convCategory.pairs.length) {
        var pair = convCategory.pairs[pairIdx - 1];
        /* If there's a result, convert it; otherwise insert conversion syntax */
        if (lastAns !== null) {
          try {
            var converted = convertValue(lastAns, convCategory.id, pair.id);
            setLastAns(converted);
            setDisplayResult(formatDecimal(converted));
            setIsResultOnDisplay(true);
          } catch { setDisplayResult('Argument ERROR'); setIsResultOnDisplay(true); }
        }
        setConvOpen(false);
        setConvCategory(null);
      }
      return;
    }

    /* ── Constants overlay ── */
    if (constsOpen) {
      if (action === 'on' || action === 'clear') { setConstsOpen(false); return; }
      if (action === 'replay-down') { setConstsPage(function(p) { return Math.min(p + 1, Math.floor((CONSTANTS.length - 1) / 10)); }); return; }
      if (action === 'replay-up') { setConstsPage(function(p) { return Math.max(p - 1, 0); }); return; }
      var constIdx = parseInt(action);
      if (constIdx >= 0 && constIdx <= 9) {
        var realIdx = constsPage * 10 + constIdx;
        if (realIdx < CONSTANTS.length) {
          var c = CONSTANTS[realIdx];
          if (f) f.typedText(String(c.value));
          setConstsOpen(false);
        }
      }
      return;
    }

    /* ── Equation mode interactions ── */
    if (currentMode === 'Equation/Func') {
      if (eqSubMode === null) {
        /* Select sub-mode */
        if (action === '1') { setEqSubMode('simul'); setEqUnknowns(2); return; }
        if (action === '2') { setEqSubMode('poly'); setEqDegree(2); return; }
        if (action === 'on' || action === 'clear') return;
        if (action === 'mode') { if (shiftMode) { setShiftMode(false); setSetupOpen(true); setSetupPage('main'); } else setMenuOpen(true); return; }
        return;
      }
      if (eqSubMode === 'simul' && eqCoeffs.length === 0) {
        /* Select number of unknowns */
        if (action === '2') { var sz = 2; setEqUnknowns(sz); setEqCoeffs(new Array(sz * (sz + 1)).fill(0)); setEqEditIdx(0); setEqInput(''); return; }
        if (action === '3') { var sz3 = 3; setEqUnknowns(sz3); setEqCoeffs(new Array(sz3 * (sz3 + 1)).fill(0)); setEqEditIdx(0); setEqInput(''); return; }
        if (action === '4') { var sz4 = 4; setEqUnknowns(sz4); setEqCoeffs(new Array(sz4 * (sz4 + 1)).fill(0)); setEqEditIdx(0); setEqInput(''); return; }
        if (action === 'clear') { setEqSubMode(null); return; }
        return;
      }
      if (eqSubMode === 'poly' && eqCoeffs.length === 0) {
        /* Select degree */
        if (action === '2') { setEqDegree(2); setEqCoeffs(new Array(3).fill(0)); setEqEditIdx(0); setEqInput(''); return; }
        if (action === '3') { setEqDegree(3); setEqCoeffs(new Array(4).fill(0)); setEqEditIdx(0); setEqInput(''); return; }
        if (action === '4') { setEqDegree(4); setEqCoeffs(new Array(5).fill(0)); setEqEditIdx(0); setEqInput(''); return; }
        if (action === 'clear') { setEqSubMode(null); return; }
        return;
      }
      /* Coefficient entry */
      if (eqCoeffs.length > 0 && eqSolutions === null) {
        if ('0123456789'.indexOf(action) >= 0) { setEqInput(function(p) { return p + action; }); return; }
        if (action === 'decimal') { setEqInput(function(p) { return p.indexOf('.') < 0 ? p + '.' : p; }); return; }
        if (action === 'subtract' || action === 'negate') { setEqInput(function(p) { return p.charAt(0) === '-' ? p.substring(1) : '-' + p; }); return; }
        if (action === 'delete') { setEqInput(function(p) { return p.length > 0 ? p.slice(0, -1) : ''; }); return; }
        if (action === 'equals' || action === 'replay-down' || action === 'replay-right') {
          /* Commit current value and advance */
          var val = parseFloat(eqInput) || 0;
          setEqCoeffs(function(prev) { var c = prev.slice(); c[eqEditIdx] = val; return c; });
          if (eqEditIdx < eqCoeffs.length - 1) {
            setEqEditIdx(function(i) { return i + 1; });
            setEqInput('');
          } else {
            /* All entered — solve */
            var newCoeffs = eqCoeffs.slice();
            newCoeffs[eqEditIdx] = val;
            try {
              if (eqSubMode === 'simul') {
                var n = eqUnknowns;
                var matrix = [];
                var consts = [];
                for (var i = 0; i < n; i++) {
                  var row = [];
                  for (var j = 0; j < n; j++) row.push(newCoeffs[i * (n + 1) + j]);
                  matrix.push(row);
                  consts.push(newCoeffs[i * (n + 1) + n]);
                }
                setEqSolutions(solveSimultaneous(matrix, consts));
              } else {
                setEqSolutions(solvePolynomial(newCoeffs));
              }
              setEqSolIdx(0);
            } catch (e) {
              setEqSolutions({ error: e.message || 'Math ERROR' });
            }
          }
          return;
        }
        if (action === 'replay-up' || action === 'replay-left') {
          if (eqEditIdx > 0) { setEqEditIdx(function(i) { return i - 1; }); setEqInput(String(eqCoeffs[eqEditIdx - 1] || '')); }
          return;
        }
        if (action === 'clear') { setEqCoeffs([]); setEqSubMode(null); setEqSolutions(null); return; }
        return;
      }
      /* Viewing solutions */
      if (eqSolutions !== null) {
        if (action === 'replay-down' || action === 'replay-right') {
          var maxIdx = Array.isArray(eqSolutions) ? eqSolutions.length - 1 : 0;
          setEqSolIdx(function(i) { return Math.min(i + 1, maxIdx); });
          return;
        }
        if (action === 'replay-up' || action === 'replay-left') {
          setEqSolIdx(function(i) { return Math.max(i - 1, 0); });
          return;
        }
        if (action === 'clear') { setEqCoeffs([]); setEqSolutions(null); setEqSubMode(null); return; }
        if (action === 'on') { setEqSolutions(null); setEqEditIdx(0); setEqInput(''); return; }
        return;
      }
      return;
    }

    /* ── Inequality mode interactions ── */
    if (currentMode === 'Inequality') {
      if (ineqType === null) {
        if (action === '1') { setIneqType('<'); return; }
        if (action === '2') { setIneqType('<='); return; }
        if (action === '3') { setIneqType('>'); return; }
        if (action === '4') { setIneqType('>='); return; }
        if (action === 'mode') { if (shiftMode) { setShiftMode(false); setSetupOpen(true); setSetupPage('main'); } else setMenuOpen(true); return; }
        return;
      }
      if (ineqCoeffs.length === 0 && ineqResult === null) {
        /* Select degree */
        if (action === '2') { setIneqDegree(2); setIneqCoeffs(new Array(3).fill(0)); setIneqEditIdx(0); setIneqInput(''); return; }
        if (action === '3') { setIneqDegree(3); setIneqCoeffs(new Array(4).fill(0)); setIneqEditIdx(0); setIneqInput(''); return; }
        if (action === '4') { setIneqDegree(4); setIneqCoeffs(new Array(5).fill(0)); setIneqEditIdx(0); setIneqInput(''); return; }
        if (action === 'clear') { setIneqType(null); return; }
        return;
      }
      /* Coefficient entry */
      if (ineqCoeffs.length > 0 && ineqResult === null) {
        if ('0123456789'.indexOf(action) >= 0) { setIneqInput(function(p) { return p + action; }); return; }
        if (action === 'decimal') { setIneqInput(function(p) { return p.indexOf('.') < 0 ? p + '.' : p; }); return; }
        if (action === 'subtract' || action === 'negate') { setIneqInput(function(p) { return p.charAt(0) === '-' ? p.substring(1) : '-' + p; }); return; }
        if (action === 'delete') { setIneqInput(function(p) { return p.length > 0 ? p.slice(0, -1) : ''; }); return; }
        if (action === 'equals' || action === 'replay-down') {
          var val = parseFloat(ineqInput) || 0;
          setIneqCoeffs(function(prev) { var c = prev.slice(); c[ineqEditIdx] = val; return c; });
          if (ineqEditIdx < ineqCoeffs.length - 1) {
            setIneqEditIdx(function(i) { return i + 1; });
            setIneqInput('');
          } else {
            var newC = ineqCoeffs.slice();
            newC[ineqEditIdx] = val;
            try {
              setIneqResult(solveInequality(newC, ineqType));
            } catch { setIneqResult({ error: 'Math ERROR' }); }
          }
          return;
        }
        if (action === 'replay-up') {
          if (ineqEditIdx > 0) { setIneqEditIdx(function(i) { return i - 1; }); setIneqInput(String(ineqCoeffs[ineqEditIdx - 1] || '')); }
          return;
        }
        if (action === 'clear') { setIneqCoeffs([]); setIneqType(null); return; }
        return;
      }
      /* Viewing result */
      if (ineqResult !== null) {
        if (action === 'clear' || action === 'on') { setIneqCoeffs([]); setIneqType(null); setIneqResult(null); return; }
        return;
      }
      return;
    }

    /* ── Ratio mode interactions ── */
    if (currentMode === 'Ratio') {
      if (ratioTemplate === null) {
        if (action === '1') { setRatioTemplate('ABxD'); setRatioValues({ A: '', B: '', C: '', D: '' }); setRatioEditIdx(0); return; }
        if (action === '2') { setRatioTemplate('ABCx'); setRatioValues({ A: '', B: '', C: '', D: '' }); setRatioEditIdx(0); return; }
        if (action === 'mode') { if (shiftMode) { setShiftMode(false); setSetupOpen(true); setSetupPage('main'); } else setMenuOpen(true); return; }
        return;
      }
      if (ratioResult === null) {
        /* Determine editable fields */
        var fields = ratioTemplate === 'ABxD' ? ['A', 'B', 'D'] : ['A', 'B', 'C'];
        var currentField = fields[ratioEditIdx] || fields[0];
        if ('0123456789'.indexOf(action) >= 0) {
          setRatioValues(function(p) { var n = Object.assign({}, p); n[currentField] = (n[currentField] || '') + action; return n; });
          return;
        }
        if (action === 'decimal') {
          setRatioValues(function(p) { var n = Object.assign({}, p); if ((n[currentField] || '').indexOf('.') < 0) n[currentField] = (n[currentField] || '') + '.'; return n; });
          return;
        }
        if (action === 'subtract' || action === 'negate') {
          setRatioValues(function(p) {
            var n = Object.assign({}, p);
            var v = n[currentField] || '';
            n[currentField] = v.charAt(0) === '-' ? v.substring(1) : '-' + v;
            return n;
          });
          return;
        }
        if (action === 'delete') {
          setRatioValues(function(p) { var n = Object.assign({}, p); n[currentField] = (n[currentField] || '').slice(0, -1); return n; });
          return;
        }
        if (action === 'equals' || action === 'replay-down') {
          if (ratioEditIdx < fields.length - 1) {
            setRatioEditIdx(function(i) { return i + 1; });
          } else {
            /* Solve */
            var A = parseFloat(ratioValues.A) || 0;
            var B = parseFloat(ratioValues.B) || 0;
            if (ratioTemplate === 'ABxD') {
              var D = parseFloat(ratioValues.D) || 0;
              if (B === 0) { setRatioResult('Math ERROR'); return; }
              setRatioResult(formatDecimal(A * D / B));
            } else {
              var C = parseFloat(ratioValues.C) || 0;
              if (A === 0) { setRatioResult('Math ERROR'); return; }
              setRatioResult(formatDecimal(B * C / A));
            }
          }
          return;
        }
        if (action === 'replay-up') { if (ratioEditIdx > 0) setRatioEditIdx(function(i) { return i - 1; }); return; }
        if (action === 'clear') { setRatioTemplate(null); setRatioResult(null); return; }
        return;
      }
      /* Viewing result */
      if (action === 'clear' || action === 'on') { setRatioTemplate(null); setRatioResult(null); return; }
      return;
    }

    /* ── Table mode interactions ── */
    if (currentMode === 'Table') {
      if (tablePhase === 'f') {
        if (action === 'equals') {
          if (f) { setTableFExpr(f.latex()); }
          if (tableFuncs === 2) setTablePhase('g');
          else setTablePhase('start');
          if (f) f.latex('');
          return;
        }
        /* Fall through to MathQuill input */
      } else if (tablePhase === 'g') {
        if (action === 'equals') {
          if (f) { setTableGExpr(f.latex()); f.latex(''); }
          setTablePhase('start');
          return;
        }
      } else if (tablePhase === 'start') {
        if ('0123456789'.indexOf(action) >= 0) { setTableStart(function(p) { return p + action; }); return; }
        if (action === 'decimal') { setTableStart(function(p) { return p.indexOf('.') < 0 ? p + '.' : p; }); return; }
        if (action === 'subtract' || action === 'negate') { setTableStart(function(p) { return p.charAt(0) === '-' ? p.substring(1) : '-' + p; }); return; }
        if (action === 'delete') { setTableStart(function(p) { return p.slice(0, -1); }); return; }
        if (action === 'equals') { setTablePhase('end'); return; }
        if (action === 'clear') { setTablePhase('f'); setTableFExpr(''); setTableGExpr(''); return; }
        return;
      } else if (tablePhase === 'end') {
        if ('0123456789'.indexOf(action) >= 0) { setTableEnd(function(p) { return p + action; }); return; }
        if (action === 'decimal') { setTableEnd(function(p) { return p.indexOf('.') < 0 ? p + '.' : p; }); return; }
        if (action === 'subtract' || action === 'negate') { setTableEnd(function(p) { return p.charAt(0) === '-' ? p.substring(1) : '-' + p; }); return; }
        if (action === 'delete') { setTableEnd(function(p) { return p.slice(0, -1); }); return; }
        if (action === 'equals') { setTablePhase('step'); return; }
        if (action === 'clear') { setTablePhase('start'); return; }
        return;
      } else if (tablePhase === 'step') {
        if ('0123456789'.indexOf(action) >= 0) { setTableStep(function(p) { return p + action; }); return; }
        if (action === 'decimal') { setTableStep(function(p) { return p.indexOf('.') < 0 ? p + '.' : p; }); return; }
        if (action === 'delete') { setTableStep(function(p) { return p.slice(0, -1); }); return; }
        if (action === 'equals') { setTablePhase('view'); return; }
        if (action === 'clear') { setTablePhase('end'); return; }
        return;
      } else if (tablePhase === 'view') {
        if (action === 'replay-down') { setTableScrollIdx(function(i) { return Math.min(i + 1, Math.max(0, tableData.length - 1)); }); return; }
        if (action === 'replay-up') { setTableScrollIdx(function(i) { return Math.max(i - 1, 0); }); return; }
        if (action === 'clear') { setTablePhase('f'); setTableData([]); return; }
        if (action === 'on') { setTablePhase('f'); setTableData([]); setTableFExpr(''); setTableGExpr(''); return; }
        return;
      }
      /* For f and g phases, fall through to normal MathQuill actions below */
    }

    if (!f && action !== 'on' && action !== 'shift' && action !== 'mode') return;

    /* Power off: SHIFT+ON */
    if (shiftMode && action === 'on') {
      setIsPoweredOn(false);
      setShiftMode(false);
      setDisplayResult(null);
      setIsResultOnDisplay(false);
      if (f) f.latex('');
      return;
    }

    /* Power on */
    if (!isPoweredOn) {
      if (action === 'on') {
        setIsPoweredOn(true);
        setSplashText('CASIO');
        setTimeout(function() { setSplashText(null); }, 1000);
        if (f) f.latex('');
        setDisplayResult(null);
        setLastAns(null);
        setMemory(null);
        setIsResultOnDisplay(false);
      }
      return;
    }

    /* ON key: clear all */
    if (action === 'on') {
      if (f) f.latex('');
      setDisplayResult(null);
      setIsResultOnDisplay(false);
      setSolveOpen(false);
      setConvOpen(false);
      setConstsOpen(false);
      return;
    }

    /* Setup menu navigation */
    if (setupOpen) {
      if (['clear', 'on'].includes(action)) { setSetupOpen(false); return; }
      if (setupPage === 'main') {
        if (action === '1') setSetupPage('angle');
        else if (action === '2') setSetupPage('output');
        else if (action === '3') setSetupPage('tablefunc');
        else if (action === 'mode') setSetupOpen(false);
        return;
      }
      if (setupPage === 'angle') {
        if (action === '1') { setAngleMode(ANGLE.DEG); setSetupOpen(false); setSetupPage('main'); }
        else if (action === '2') { setAngleMode(ANGLE.RAD); setSetupOpen(false); setSetupPage('main'); }
        else if (action === '3') { setAngleMode(ANGLE.GRAD); setSetupOpen(false); setSetupPage('main'); }
        else if (action === 'mode') { setSetupOpen(false); setSetupPage('main'); }
        return;
      }
      if (setupPage === 'output') {
        if (action === '1') { setOutputMode('math'); setSetupOpen(false); setSetupPage('main'); }
        else if (action === '2') { setOutputMode('decimal8'); setSetupOpen(false); setSetupPage('main'); }
        else if (action === '3') { setOutputMode('decimal16'); setSetupOpen(false); setSetupPage('main'); }
        else if (action === 'mode') { setSetupOpen(false); setSetupPage('main'); }
        return;
      }
      if (setupPage === 'tablefunc') {
        if (action === '1') { setTableFuncs(1); setSetupOpen(false); setSetupPage('main'); }
        else if (action === '2') { setTableFuncs(2); setSetupOpen(false); setSetupPage('main'); }
        else if (action === 'mode') { setSetupOpen(false); setSetupPage('main'); }
        return;
      }
      return;
    }

    /* If result is on display, handle appropriately */
    if (isResultOnDisplay) {
      var editActions = ['delete', 'replay-left', 'replay-right'];
      var passThrough = ['shift', 'hyp', 'mode', 'on', 'clear', 'equals', 'sd',
                          'sto', 'rcl', 'ans', 'replay-up', 'replay-down', 'alpha',
                          'optn', 'eng', 'dms'];
      var opActions = ['add', 'subtract', 'multiply', 'divide'];
      var postfixActions = ['cube', 'square', 'power', 'reciprocal'];

      if (editActions.includes(action)) {
        setIsResultOnDisplay(false);
      } else if (!passThrough.includes(action)) {
        if (opActions.includes(action) ||
            (postfixActions.includes(action) && !((action === 'square' || action === 'power') && shiftMode))) {
          if (f) { f.latex(''); f.typedText('Ans'); }
          setIsResultOnDisplay(false);
        } else {
          if (f) f.latex('');
          setIsResultOnDisplay(false);
        }
      }
    }

    /* SHIFT mode actions */
    var consumedShift = true;
    if (shiftMode) {
      switch (action) {
        case 'sqrt': f.write('\\sqrt[3]{}'); f.keystroke('Left'); break;
        case 'square': f.write('^3'); break;
        case 'power': f.write('\\sqrt[]{}'); f.keystroke('Left Left'); break;
        case 'log':
          if (f.latex().trim() !== '') f.cmd('\\times');
          f.typedText('10'); f.cmd('^');
          break;
        case 'ln': f.typedText('e'); f.cmd('^'); break;
        case 'sin':
          f.write(hypMode ? '\\sinh^{-1}()' : '\\sin^{-1}()');
          f.keystroke('Left');
          setHypMode(false);
          break;
        case 'cos':
          f.write(hypMode ? '\\cosh^{-1}()' : '\\cos^{-1}()');
          f.keystroke('Left');
          setHypMode(false);
          break;
        case 'tan':
          f.write(hypMode ? '\\tanh^{-1}()' : '\\tan^{-1}()');
          f.keystroke('Left');
          setHypMode(false);
          break;
        case 'fraction':
          f.typedText(' ');
          f.cmd('\\frac');
          break;
        case '7':
          setConstsOpen(true); setConstsPage(0);
          break;
        case '8':
          setConvOpen(true); setConvCategory(null);
          break;
        case 'ans':
          setAngleMode(function(prev) {
            var modes = [ANGLE.DEG, ANGLE.RAD, ANGLE.GRAD];
            return modes[(modes.indexOf(prev) + 1) % 3];
          });
          break;
        case 'equals': f.typedText('%'); break;
        case 'negate': f.write('!'); break;
        case 'paren-open': f.typedText('abs('); break;
        case 'paren-close': f.typedText(','); break;
        case 'sto':
          if (f) f.typedText('M');
          break;
        case 'multiply': f.typedText('nPr('); break;
        case 'divide': f.typedText('nCr('); break;
        case 'sd':
          if (lastAns !== null) setMemory(function(prev) { return (prev || 0) + lastAns; });
          break;
        case 'mplus':
          if (lastAns !== null) setMemory(function(prev) { return (prev || 0) - lastAns; });
          break;
        case 'decimal': f.typedText('random()'); break;
        case 'exp': f.cmd('\\pi'); break;
        case 'delete': break;
        case 'calc':
          /* SHIFT+CALC → SOLVE */
          var currentLatex = f ? f.latex() : '';
          if (currentLatex.trim()) {
            var sExpr = latexToExpr(currentLatex);
            var parsedEq = parseEquation(sExpr);
            var sVars = findVariables(parsedEq);
            setSolveExpr(parsedEq);
            setSolveVar(sVars.length > 0 ? sVars[0] : 'x');
            setSolveGuess('0');
            setSolveStep(sVars.length > 1 ? 'var' : 'guess');
            setSolveResult(null);
            setSolveOpen(true);
          }
          break;
        case 'add': f.typedText('Pol('); break;
        case 'subtract': f.typedText('Rec('); break;
        default: consumedShift = false; break;
      }
      if (consumedShift) { setShiftMode(false); return; }
    }

    /* Main actions */
    switch (action) {
      case 'shift':
        setShiftMode(function(s) { return !s; });
        setAlphaMode(false);
        break;
      case 'alpha':
        setAlphaMode(function(a) { return !a; });
        setShiftMode(false);
        break;
      case 'mode':
        if (shiftMode) {
          setShiftMode(false);
          setSetupOpen(true);
          setSetupPage('main');
        } else {
          setMenuOpen(function(m) { return !m; });
        }
        break;
      case 'clear':
        if (f) f.latex('');
        setDisplayResult(null);
        setIsResultOnDisplay(false);
        break;

      /* Digits */
      case '0': case '1': case '2': case '3': case '4':
      case '5': case '6': case '7': case '8': case '9':
        if (alphaMode) {
          var alphaMap = {
            '7': 'A', '8': 'B', '9': 'C',
            '4': 'D', '5': 'E', '6': 'F',
            '1': 'x', '2': 'y', '3': ':'
          };
          if (alphaMap[action]) {
            f.typedText(alphaMap[action]);
            setAlphaMode(false);
            break;
          }
        }
        f.typedText(action);
        break;

      /* Operators */
      case 'add': f.cmd('+'); break;
      case 'subtract': f.cmd('-'); break;
      case 'multiply': f.cmd('\\times'); break;
      case 'divide': f.cmd('\\div'); break;
      case 'decimal': f.typedText('.'); break;

      case 'paren-open': f.typedText('('); break;
      case 'paren-close': f.typedText(')'); break;

      case 'delete': f.keystroke('Backspace'); break;

      /* Functions */
      case 'sqrt': f.cmd('\\sqrt'); break;
      case 'square': f.write('^2'); break;
      case 'cube': f.write('^3'); break;
      case 'power': f.cmd('^'); break;
      case 'reciprocal': f.write('^{-1}'); break;

      case 'hyp':
        setHypMode(function(h) { return !h; });
        break;

      case 'sin':
      case 'cos':
      case 'tan':
        f.write('\\' + (hypMode ? action + 'h' : action) + '()');
        f.keystroke('Left');
        if (hypMode) setHypMode(false);
        break;

      case 'log': f.write('\\log()'); f.keystroke('Left'); break;
      case 'ln': f.write('\\ln()'); f.keystroke('Left'); break;

      case 'fraction': f.cmd('\\frac'); break;

      case 'negate': f.typedText('-'); break;

      case 'exp':
        if (f.latex().trim() === '') f.typedText('Ans');
        f.cmd('\\times');
        f.typedText('10');
        f.cmd('^');
        break;

      case 'pi': f.cmd('\\pi'); break;
      case 'abs': f.typedText('abs('); break;

      case 'ans':
        if (isResultOnDisplay && f) f.latex('');
        f.typedText('Ans');
        setIsResultOnDisplay(false);
        break;

      case 'sto': {
        if (lastAns !== null) setMemory(lastAns);
        break;
      }

      case 'rcl':
        f.typedText('M');
        break;

      case 'mplus':
        if (lastAns !== null) setMemory(function(prev) { return (prev || 0) + lastAns; });
        break;

      case 'optn':
        break;

      case 'calc':
        break;

      case 'eng': {
        if (lastAns !== null) {
          var eng = toEngineering(lastAns, 'left');
          var engStr = eng.mantissa.toPrecision(10).replace(/\.?0+$/, '') + (eng.exponent !== 0 ? '\u00D710^' + eng.exponent : '');
          setDisplayResult(engStr);
        }
        break;
      }

      case 'dms': {
        if (lastAns !== null) {
          setDisplayResult(formatDMS(lastAns));
        }
        break;
      }

      case 'sd': {
        if (lastAns === null) break;
        var cycle = getSDCycle(lastAns);
        if (cycle.length <= 1) break;
        var nextIdx = (sdMode + 1) % cycle.length;
        setSdMode(nextIdx);
        setDisplayResult(cycle[nextIdx]);
        break;
      }

      case 'replay-left': f.keystroke('Left'); break;
      case 'replay-right': f.keystroke('Right'); break;
      case 'replay-up': {
        if (history.length === 0) break;
        setHistoryIndex(function(prev) {
          var idx;
          if (prev === -1) idx = history.length - 1;
          else idx = Math.max(0, prev - 1);
          if (field()) field().latex(history[idx].latex);
          setIsResultOnDisplay(false);
          setDisplayResult(null);
          return idx;
        });
        break;
      }
      case 'replay-down': {
        if (historyIndex === -1) {
          f.keystroke('Down');
          break;
        }
        setHistoryIndex(function(prev) {
          var idx = Math.min(history.length - 1, prev + 1);
          if (field()) field().latex(history[idx].latex);
          setIsResultOnDisplay(false);
          setDisplayResult(null);
          return idx;
        });
        break;
      }

      /* Equals - EVALUATE */
      case 'equals': {
        if (!f) break;
        var latex = f.latex();
        if (!latex.trim()) break;

        setHistory(function(prev) {
          var newH = (prev.length === 0 || prev[prev.length - 1].latex !== latex)
            ? prev.concat([{ latex: latex }])
            : prev;
          return newH.slice(-30);
        });
        setHistoryIndex(-1);

        try {
          var exprStr = latexToExpr(latex);
          var allVars = Object.assign({}, calcVars, { Ans: lastAns || 0, M: memory || 0, PreAns: preAns || 0 });
          var evalResult = evalWithAngle(exprStr, angleMode, allVars);

          if (!isFinite(evalResult)) throw new Error('Math ERROR');

          setPreAns(lastAns);
          setLastAns(evalResult);
          setSdMode(0);

          var formatted = formatMathResult(evalResult, outputMode !== 'math', outputMode);
          setDisplayResult(formatted);
          setIsResultOnDisplay(true);
        } catch (err) {
          var msg = err.message || 'Syntax ERROR';
          setDisplayResult(msg.indexOf('ERROR') >= 0 || msg.indexOf("Can't") >= 0 ? msg : 'Syntax ERROR');
          setIsResultOnDisplay(true);
        }
        setShiftMode(false);
        setAlphaMode(false);
        break;
      }

      default:
        break;
    }

    if (!['shift', 'alpha', 'hyp'].includes(action)) {
      if (shiftMode && consumedShift) setShiftMode(false);
      if (alphaMode && !['0','1','2','3','4','5','6','7','8','9'].includes(action)) {
        setAlphaMode(false);
      }
    }
  }, [shiftMode, alphaMode, hypMode, angleMode, isPoweredOn, setupOpen, setupPage,
      isResultOnDisplay, lastAns, memory, history, historyIndex, sdMode, outputMode,
      calcVars, preAns, currentMode, solveOpen, solveExpr, solveVar, solveGuess, solveStep,
      convOpen, convCategory, constsOpen, constsPage,
      eqSubMode, eqCoeffs, eqEditIdx, eqInput, eqSolutions, eqSolIdx, eqUnknowns, eqDegree,
      ineqType, ineqDegree, ineqCoeffs, ineqEditIdx, ineqInput, ineqResult,
      ratioTemplate, ratioValues, ratioEditIdx, ratioResult,
      tableFuncs, tableFExpr, tableGExpr, tableStart, tableEnd, tableStep, tablePhase, tableData, tableScrollIdx]);

  /* Generate table data when entering view phase */
  useEffect(function() {
    if (tablePhase === 'view' && tableData.length === 0) {
      generateTable();
    }
  }, [tablePhase]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Physical keyboard */
  useEffect(function() {
    var onKey = function(e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      var k = KEY_MAP[e.key];
      if (k) { e.preventDefault(); handleAction(k); }
    };
    window.addEventListener('keydown', onKey);
    return function() { window.removeEventListener('keydown', onKey); };
  }, [handleAction]);

  /* Button renderer */
  var btn = function(label, action, cls, shiftL, alphaL) {
    return (
      <button
        key={action + label}
        className={'ck ' + (cls || '') + (action === 'shift' && shiftMode ? ' active' : '') + (action === 'alpha' && alphaMode ? ' active' : '')}
        onClick={function() { handleAction(action); }}
        tabIndex={-1}
      >
        {shiftL && <span className="sl">{shiftL}</span>}
        {alphaL && <span className="al">{alphaL}</span>}
        <span className="ml">{label}</span>
      </button>
    );
  };

  /* Mode selection */
  var selectMode = useCallback(function(mode) {
    setMenuOpen(false);
    setCurrentMode(mode.name);
    if (mqRef.current) mqRef.current.latex('');
    setDisplayResult(null);
    setIsResultOnDisplay(false);
    setSolveOpen(false);
    setConvOpen(false);
    setConstsOpen(false);

    if (mode.name === 'Equation/Func') {
      setEqSubMode(null);
      setEqSolutions(null);
      setEqCoeffs([]);
    } else if (mode.name === 'Inequality') {
      setIneqType(null);
      setIneqResult(null);
      setIneqCoeffs([]);
    } else if (mode.name === 'Ratio') {
      setRatioTemplate(null);
      setRatioResult(null);
    } else if (mode.name === 'Table') {
      setTablePhase('f');
      setTableData([]);
      setTableFExpr('');
      setTableGExpr('');
      setTableStart('');
      setTableEnd('');
      setTableStep('');
    }
  }, []);

  /* ═══════════════════════════════════════════════
     LCD content renderers
     ═══════════════════════════════════════════════ */

  var renderSetupOnLCD = function() {
    if (!setupOpen) return null;
    return (
      <div className="lcd-setup-overlay">
        {setupPage === 'main' && (
          <div className="lcd-setup-menu">
            <div className="lcd-setup-item" onClick={function() { setSetupPage('angle'); }}>1: Angle Unit</div>
            <div className="lcd-setup-item" onClick={function() { setSetupPage('output'); }}>2: Output Format</div>
            <div className="lcd-setup-item" onClick={function() { setSetupPage('tablefunc'); }}>3: Table Functions</div>
          </div>
        )}
        {setupPage === 'angle' && (
          <div className="lcd-setup-menu">
            <div className={'lcd-setup-item' + (angleMode === ANGLE.DEG ? ' lcd-setup-active' : '')}
                 onClick={function() { setAngleMode(ANGLE.DEG); setSetupOpen(false); setSetupPage('main'); }}>
              1: Degrees
            </div>
            <div className={'lcd-setup-item' + (angleMode === ANGLE.RAD ? ' lcd-setup-active' : '')}
                 onClick={function() { setAngleMode(ANGLE.RAD); setSetupOpen(false); setSetupPage('main'); }}>
              2: Radians
            </div>
            <div className={'lcd-setup-item' + (angleMode === ANGLE.GRAD ? ' lcd-setup-active' : '')}
                 onClick={function() { setAngleMode(ANGLE.GRAD); setSetupOpen(false); setSetupPage('main'); }}>
              3: Gradians
            </div>
          </div>
        )}
        {setupPage === 'output' && (
          <div className="lcd-setup-menu">
            <div className={'lcd-setup-item' + (outputMode === 'math' ? ' lcd-setup-active' : '')}
                 onClick={function() { setOutputMode('math'); setSetupOpen(false); setSetupPage('main'); }}>
              1: Normal
            </div>
            <div className={'lcd-setup-item' + (outputMode === 'decimal8' ? ' lcd-setup-active' : '')}
                 onClick={function() { setOutputMode('decimal8'); setSetupOpen(false); setSetupPage('main'); }}>
              2: Decimal (8 d.p.)
            </div>
            <div className={'lcd-setup-item' + (outputMode === 'decimal16' ? ' lcd-setup-active' : '')}
                 onClick={function() { setOutputMode('decimal16'); setSetupOpen(false); setSetupPage('main'); }}>
              3: Decimal (16 d.p.)
            </div>
          </div>
        )}
        {setupPage === 'tablefunc' && (
          <div className="lcd-setup-menu">
            <div className={'lcd-setup-item' + (tableFuncs === 1 ? ' lcd-setup-active' : '')}
                 onClick={function() { setTableFuncs(1); setSetupOpen(false); setSetupPage('main'); }}>
              1: f(x)
            </div>
            <div className={'lcd-setup-item' + (tableFuncs === 2 ? ' lcd-setup-active' : '')}
                 onClick={function() { setTableFuncs(2); setSetupOpen(false); setSetupPage('main'); }}>
              2: f(x), g(x)
            </div>
          </div>
        )}
      </div>
    );
  };

  /* Equation mode LCD */
  var renderEquationLCD = function() {
    if (eqSubMode === null) {
      return (
        <div className="mode-lcd-panel">
          <div className="mode-lcd-title">Equation/Func</div>
          <div className="mode-lcd-option" onClick={function() { setEqSubMode('simul'); }}>1: Simul Equation</div>
          <div className="mode-lcd-option" onClick={function() { setEqSubMode('poly'); }}>2: Polynomial</div>
        </div>
      );
    }
    if (eqSubMode === 'simul' && eqCoeffs.length === 0) {
      return (
        <div className="mode-lcd-panel">
          <div className="mode-lcd-title">Unknowns?</div>
          <div className="mode-lcd-option" onClick={function() { handleAction('2'); }}>2: 2 Unknowns</div>
          <div className="mode-lcd-option" onClick={function() { handleAction('3'); }}>3: 3 Unknowns</div>
          <div className="mode-lcd-option" onClick={function() { handleAction('4'); }}>4: 4 Unknowns</div>
        </div>
      );
    }
    if (eqSubMode === 'poly' && eqCoeffs.length === 0) {
      return (
        <div className="mode-lcd-panel">
          <div className="mode-lcd-title">Degree?</div>
          <div className="mode-lcd-option" onClick={function() { handleAction('2'); }}>2: ax\u00B2+bx+c=0</div>
          <div className="mode-lcd-option" onClick={function() { handleAction('3'); }}>3: ax\u00B3+bx\u00B2+cx+d=0</div>
          <div className="mode-lcd-option" onClick={function() { handleAction('4'); }}>4: Degree 4</div>
        </div>
      );
    }
    /* Coefficient entry grid */
    if (eqCoeffs.length > 0 && eqSolutions === null) {
      var labels = [];
      if (eqSubMode === 'simul') {
        var varNames = ['x', 'y', 'z', 'w'];
        for (var r = 0; r < eqUnknowns; r++) {
          for (var c = 0; c <= eqUnknowns; c++) {
            if (c < eqUnknowns) labels.push('a' + (r + 1) + varNames[c]);
            else labels.push('=' + (r + 1));
          }
        }
      } else {
        var degree = eqDegree;
        for (var d = degree; d >= 0; d--) {
          if (d === 0) labels.push('const');
          else if (d === 1) labels.push('x');
          else labels.push('x\u00B2'.replace('2', String(d)));
        }
      }
      return (
        <div className="mode-lcd-panel">
          <div className="mode-lcd-title">{eqSubMode === 'simul' ? 'Coefficients' : 'Polynomial'}</div>
          <div className="eq-coeff-grid">
            {eqCoeffs.map(function(val, idx) {
              var isActive = idx === eqEditIdx;
              return (
                <div key={idx} className={'eq-coeff-cell' + (isActive ? ' eq-active' : '')}
                     onClick={function() { setEqEditIdx(idx); setEqInput(String(val || '')); }}>
                  <span className="eq-coeff-label">{labels[idx] || ''}</span>
                  <span className="eq-coeff-val">{isActive ? (eqInput || '_') : (val || 0)}</span>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    /* Solutions display */
    if (eqSolutions !== null) {
      if (eqSolutions.error) {
        return (
          <div className="mode-lcd-panel">
            <div className="mode-lcd-title lcd-err">{eqSolutions.error}</div>
          </div>
        );
      }
      var sols = Array.isArray(eqSolutions) ? eqSolutions : [];
      return (
        <div className="mode-lcd-panel">
          <div className="mode-lcd-title">Solutions ({eqSolIdx + 1}/{sols.length})</div>
          <div className="eq-solution">
            {eqSubMode === 'simul' ? (
              <span>{['x', 'y', 'z', 'w'][eqSolIdx]} = {formatComplexSolution(sols[eqSolIdx])}</span>
            ) : (
              <span>x{eqSolIdx + 1} = {formatComplexSolution(sols[eqSolIdx])}</span>
            )}
          </div>
          <div className="eq-nav-hint">{'\u25B2\u25BC'} navigate</div>
        </div>
      );
    }
    return null;
  };

  /* Inequality mode LCD */
  var renderInequalityLCD = function() {
    if (ineqType === null) {
      return (
        <div className="mode-lcd-panel">
          <div className="mode-lcd-title">Inequality Type</div>
          <div className="mode-lcd-option" onClick={function() { setIneqType('<'); }}>1: f(x) {'<'} 0</div>
          <div className="mode-lcd-option" onClick={function() { setIneqType('<='); }}>2: f(x) {'\u2264'} 0</div>
          <div className="mode-lcd-option" onClick={function() { setIneqType('>'); }}>3: f(x) {'>'} 0</div>
          <div className="mode-lcd-option" onClick={function() { setIneqType('>='); }}>4: f(x) {'\u2265'} 0</div>
        </div>
      );
    }
    if (ineqCoeffs.length === 0 && ineqResult === null) {
      return (
        <div className="mode-lcd-panel">
          <div className="mode-lcd-title">Degree?</div>
          <div className="mode-lcd-option" onClick={function() { handleAction('2'); }}>2: Degree 2</div>
          <div className="mode-lcd-option" onClick={function() { handleAction('3'); }}>3: Degree 3</div>
          <div className="mode-lcd-option" onClick={function() { handleAction('4'); }}>4: Degree 4</div>
        </div>
      );
    }
    if (ineqCoeffs.length > 0 && ineqResult === null) {
      var labels = [];
      for (var d = ineqDegree; d >= 0; d--) {
        if (d === 0) labels.push('const');
        else if (d === 1) labels.push('x');
        else labels.push('x^' + d);
      }
      return (
        <div className="mode-lcd-panel">
          <div className="mode-lcd-title">Coefficients ({ineqType})</div>
          <div className="eq-coeff-grid">
            {ineqCoeffs.map(function(val, idx) {
              var isActive = idx === ineqEditIdx;
              return (
                <div key={idx} className={'eq-coeff-cell' + (isActive ? ' eq-active' : '')}
                     onClick={function() { setIneqEditIdx(idx); setIneqInput(String(val || '')); }}>
                  <span className="eq-coeff-label">{labels[idx] || ''}</span>
                  <span className="eq-coeff-val">{isActive ? (ineqInput || '_') : (val || 0)}</span>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    if (ineqResult !== null) {
      if (ineqResult.error) {
        return (
          <div className="mode-lcd-panel">
            <div className="mode-lcd-title lcd-err">{ineqResult.error}</div>
          </div>
        );
      }
      var formatInterval = function(iv) {
        var loStr = iv.lo === null ? '\u2212\u221E' : formatDecimal(iv.lo);
        var hiStr = iv.hi === null ? '\u221E' : formatDecimal(iv.hi);
        var loB = iv.loInclusive && iv.lo !== null ? '\u2264' : '<';
        var hiB = iv.hiInclusive && iv.hi !== null ? '\u2264' : '<';
        if (iv.lo === null) return 'x ' + hiB + ' ' + hiStr;
        if (iv.hi === null) return loStr + ' ' + loB + ' x';
        return loStr + ' ' + loB + ' x ' + hiB + ' ' + hiStr;
      };
      return (
        <div className="mode-lcd-panel">
          <div className="mode-lcd-title">Solution</div>
          <div className="ineq-result">
            {ineqResult.intervals && ineqResult.intervals.length > 0 ?
              ineqResult.intervals.map(function(iv, i) {
                return <div key={i}>{formatInterval(iv)}</div>;
              }) :
              <div>No solution</div>
            }
          </div>
        </div>
      );
    }
    return null;
  };

  /* Ratio mode LCD */
  var renderRatioLCD = function() {
    if (ratioTemplate === null) {
      return (
        <div className="mode-lcd-panel">
          <div className="mode-lcd-title">Ratio</div>
          <div className="mode-lcd-option" onClick={function() { setRatioTemplate('ABxD'); }}>1: A:B = X:D</div>
          <div className="mode-lcd-option" onClick={function() { setRatioTemplate('ABCx'); }}>2: A:B = C:X</div>
        </div>
      );
    }
    if (ratioResult !== null) {
      return (
        <div className="mode-lcd-panel">
          <div className="mode-lcd-title">X = {ratioResult}</div>
          <div className="eq-nav-hint">AC to clear</div>
        </div>
      );
    }
    var fields = ratioTemplate === 'ABxD' ? ['A', 'B', 'D'] : ['A', 'B', 'C'];
    var currentField = fields[ratioEditIdx] || fields[0];
    var templateStr = ratioTemplate === 'ABxD' ? 'A : B = X : D' : 'A : B = C : X';
    return (
      <div className="mode-lcd-panel">
        <div className="mode-lcd-title">{templateStr}</div>
        <div className="ratio-grid">
          {fields.map(function(key, idx) {
            var isActive = idx === ratioEditIdx;
            return (
              <div key={key} className={'eq-coeff-cell' + (isActive ? ' eq-active' : '')}>
                <span className="eq-coeff-label">{key}</span>
                <span className="eq-coeff-val">{isActive ? (ratioValues[currentField] || '_') : (ratioValues[key] || '0')}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /* Table mode LCD */
  var renderTableLCD = function() {
    if (tablePhase === 'f' || tablePhase === 'g') {
      return null; /* Uses MathQuill — rendered normally */
    }
    if (tablePhase === 'start') {
      return (
        <div className="mode-lcd-panel">
          <div className="mode-lcd-title">Start?</div>
          <div className="table-input-val">{tableStart || '_'}</div>
        </div>
      );
    }
    if (tablePhase === 'end') {
      return (
        <div className="mode-lcd-panel">
          <div className="mode-lcd-title">End?</div>
          <div className="table-input-val">{tableEnd || '_'}</div>
        </div>
      );
    }
    if (tablePhase === 'step') {
      return (
        <div className="mode-lcd-panel">
          <div className="mode-lcd-title">Step?</div>
          <div className="table-input-val">{tableStep || '_'}</div>
        </div>
      );
    }
    if (tablePhase === 'view') {
      var cols = tableFuncs === 2 ? ['x', 'f(x)', 'g(x)'] : ['x', 'f(x)'];
      var startRow = Math.max(0, tableScrollIdx - 2);
      var endRow = Math.min(tableData.length, startRow + 6);
      var visibleRows = tableData.slice(startRow, endRow);
      return (
        <div className="mode-lcd-panel table-view">
          <div className="table-header">
            {cols.map(function(c) { return <span key={c} className="table-hcell">{c}</span>; })}
          </div>
          <div className="table-body">
            {visibleRows.map(function(row, i) {
              var realIdx = startRow + i;
              var isActive = realIdx === tableScrollIdx;
              return (
                <div key={realIdx} className={'table-row' + (isActive ? ' table-active' : '')}>
                  <span className="table-cell">{typeof row.x === 'number' ? formatDecimal(row.x, 6) : row.x}</span>
                  <span className="table-cell">{typeof row.fx === 'number' ? formatDecimal(row.fx, 6) : String(row.fx)}</span>
                  {tableFuncs === 2 && <span className="table-cell">{row.gx !== undefined ? (typeof row.gx === 'number' ? formatDecimal(row.gx, 6) : String(row.gx)) : ''}</span>}
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    return null;
  };

  var angleIndicator = { DEG: 'D', RAD: 'R', GRAD: 'G' };

  /* Determine what to show in the LCD input area */
  var renderLCDInput = function() {
    if (setupOpen) return renderSetupOnLCD();
    if (currentMode === 'Equation/Func' && eqSubMode !== null) return renderEquationLCD();
    if (currentMode === 'Equation/Func' && eqSubMode === null) return renderEquationLCD();
    if (currentMode === 'Inequality') return renderInequalityLCD();
    if (currentMode === 'Ratio') return renderRatioLCD();
    if (currentMode === 'Table' && tablePhase !== 'f' && tablePhase !== 'g') return renderTableLCD();
    /* Default: MathQuill field */
    return <div ref={mqElRef} className="mq-field" />;
  };

  /* LCD prompt text for Table mode f/g input */
  var tableModeLabel = function() {
    if (currentMode !== 'Table') return null;
    if (tablePhase === 'f') return 'f(x)=';
    if (tablePhase === 'g') return 'g(x)=';
    return null;
  };

  return (
    <div className="calc-page" ref={shellRef} tabIndex={0}>
      <div className="calc-body">

        <div className="brand">
          <span className="brand-casio">CASIO</span>
          <span className="brand-cw">CLASSWIZ</span>
        </div>

        <div className="lcd">
          {splashText ? (
            <div className="lcd-splash">{splashText}</div>
          ) : !isPoweredOn ? (
            <div className="lcd-off" />
          ) : (
            <>
              <div className="lcd-status">
                <span className={shiftMode ? 'on' : ''}>S</span>
                <span className={alphaMode ? 'on' : ''}>A</span>
                <span className={memory !== null && memory !== 0 ? 'on' : ''}>M</span>
                <span className="ang">{angleIndicator[angleMode]}</span>
                <span className={hypMode ? 'on' : ''}>HYP</span>
                <span className="mode-label">{currentMode}</span>
              </div>

              <div className="lcd-input mq-input-wrapper">
                {tableModeLabel() && <span className="table-func-label">{tableModeLabel()}</span>}
                {renderLCDInput()}
              </div>

              <div className={'lcd-output ' + resultSizeClass() + (typeof displayResult === 'string' && displayResult.indexOf('ERROR') >= 0 ? ' lcd-err' : '')}>
                {displayResult !== null ? <ResultDisplay result={displayResult} /> : '\u00A0'}
              </div>
            </>
          )}
        </div>

        <div className="nav-row">
          {btn('SHIFT', 'shift', 'k-shift')}
          {btn('ALPHA', 'alpha', 'k-alpha')}

          <div className="dpad">
            <button className="dp dp-u" onClick={function() { handleAction('replay-up'); }} tabIndex={-1}>{'\u25B2'}</button>
            <button className="dp dp-l" onClick={function() { handleAction('replay-left'); }} tabIndex={-1}>{'\u25C0'}</button>
            <div className="dp-center" />
            <button className="dp dp-r" onClick={function() { handleAction('replay-right'); }} tabIndex={-1}>{'\u25B6'}</button>
            <button className="dp dp-d" onClick={function() { handleAction('replay-down'); }} tabIndex={-1}>{'\u25BC'}</button>
          </div>

          <div className="nav-right">
            <button className="ck k-menu" onClick={function() { handleAction('mode'); }} tabIndex={-1}>
              <span className="sl">SETUP</span>
              <span className="ml">MENU</span>
            </button>
            <button className="ck k-on" onClick={function() { handleAction('on'); }} tabIndex={-1}>
              <span className="ml">ON</span>
            </button>
          </div>
        </div>

        <div className="optn-row">
          {btn('OPTN', 'optn', 'k-dark', 'QR')}
          {btn('CALC', 'calc', 'k-dark', 'SOLVE')}
        </div>

        <div className="key-grid">
          <div className="kr kr6">
            {btn('\uD835\uDC4E \u1D47\u2044c', 'fraction', 'k-fn', '\uD835\uDC51/\uD835\uDC50')}
            {btn('\u221A', 'sqrt', 'k-fn', '\u221B')}
            {btn('x\u00B2', 'square', 'k-fn', 'x\u00B3')}
            {btn('x\u25A1', 'power', 'k-fn', '\u02E3\u221A')}
            {btn('log', 'log', 'k-fn', '10\u02E3')}
            {btn('ln', 'ln', 'k-fn', 'e\u02E3')}
          </div>

          <div className="kr kr6">
            {btn('(\u2212)', 'negate', 'k-fn', 'x!')}
            {btn('\u00B0\u2032\u2033', 'dms', 'k-fn')}
            {btn('hyp', 'hyp', 'k-fn')}
            {btn('sin', 'sin', 'k-fn', 'sin\u207B\u00B9')}
            {btn('cos', 'cos', 'k-fn', 'cos\u207B\u00B9')}
            {btn('tan', 'tan', 'k-fn', 'tan\u207B\u00B9')}
          </div>

          <div className="kr kr6">
            {btn('STO', 'sto', 'k-fn', 'RCL')}
            {btn('ENG', 'eng', 'k-fn', '\u2190ENG')}
            {btn('(', 'paren-open', 'k-fn', 'Abs(')}
            {btn(')', 'paren-close', 'k-fn', ',')}
            {btn('S\u27F7D', 'sd', 'k-fn', 'M+')}
            {btn('M+', 'mplus', 'k-fn', 'M\u2212')}
          </div>

          <div className="kr kr5">
            {btn('7', '7', 'k-num', 'CONST', 'A')}
            {btn('8', '8', 'k-num', 'CONV', 'B')}
            {btn('9', '9', 'k-num', null, 'C')}
            {btn('DEL', 'delete', 'k-del', 'INS')}
            {btn('AC', 'clear', 'k-ac')}
          </div>

          <div className="kr kr5">
            {btn('4', '4', 'k-num', null, 'D')}
            {btn('5', '5', 'k-num', null, 'E')}
            {btn('6', '6', 'k-num', null, 'F')}
            {btn('\u00D7', 'multiply', 'k-op', 'nPr')}
            {btn('\u00F7', 'divide', 'k-op', 'nCr')}
          </div>

          <div className="kr kr5">
            {btn('1', '1', 'k-num', null, 'x')}
            {btn('2', '2', 'k-num', null, 'y')}
            {btn('3', '3', 'k-num', null, ':')}
            {btn('+', 'add', 'k-op', 'Pol(')}
            {btn('\u2212', 'subtract', 'k-op', 'Rec(')}
          </div>

          <div className="kr kr5">
            {btn('0', '0', 'k-num')}
            {btn('\u00B7', 'decimal', 'k-num', 'Ran#')}
            {btn('\u00D710\u02E3', 'exp', 'k-num', '\u03C0')}
            {btn('Ans', 'ans', 'k-num', 'PreAns', 'PreAns')}
            {btn('=', 'equals', 'k-eq', '%')}
          </div>
        </div>

        <div className="model-label">fx-991EX</div>
      </div>

      {/* ═══ MODE SELECTION OVERLAY ═══ */}
      {menuOpen && (
        <div className="menu-overlay" onClick={function() { setMenuOpen(false); }}>
          <div className="menu-panel" onClick={function(e) { e.stopPropagation(); }}>
            <div className="menu-title">MODE</div>
            <div className="menu-grid">
              {MODES.map(function(m) {
                return (
                  <button
                    key={m.id}
                    className={'menu-mode' + (currentMode === m.name ? ' menu-active' : '')}
                    onClick={function() { selectMode(m); }}
                  >
                    <span className="menu-icon">{m.icon}</span>
                    <span className="menu-name">{m.name}</span>
                    <span className="menu-sc">{m.shortcut}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ═══ SOLVE OVERLAY ═══ */}
      {solveOpen && (
        <div className="overlay-panel solve-panel">
          <div className="overlay-title">SOLVE</div>
          {solveStep === 'var' && (
            <div className="solve-content">
              <div className="solve-label">Select variable:</div>
              <div className="solve-var-options">
                {['x', 'y', 'A', 'B', 'C', 'D'].map(function(v, i) {
                  return <button key={v} className={'solve-var-btn' + (solveVar === v ? ' active' : '')} onClick={function() { setSolveVar(v); setSolveStep('guess'); }}>{i + 1}: {v}</button>;
                })}
              </div>
            </div>
          )}
          {solveStep === 'guess' && (
            <div className="solve-content">
              <div className="solve-label">Solve for: {solveVar}</div>
              <div className="solve-label">Initial guess:</div>
              <div className="solve-input-display">{solveGuess || '0'}</div>
              <div className="solve-hint">Enter guess then press =</div>
            </div>
          )}
          {solveStep === 'result' && solveResult && (
            <div className="solve-content">
              {solveResult.error ? (
                <div className="solve-label lcd-err">{solveResult.error}</div>
              ) : (
                <>
                  <div className="solve-label">{solveVar} = {formatDecimal(solveResult.solution)}</div>
                  <div className="solve-lmr">L\u2212R = {solveResult.lMinusR.toExponential(4)}</div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═══ CONVERSION OVERLAY ═══ */}
      {convOpen && (
        <div className="overlay-panel conv-panel">
          <div className="overlay-title">CONV</div>
          {convCategory === null ? (
            <div className="conv-list">
              {CONV_CATEGORIES.map(function(cat) {
                return <button key={cat.id} className="conv-item" onClick={function() { setConvCategory(cat); }}>{cat.id}: {cat.name}</button>;
              })}
            </div>
          ) : (
            <div className="conv-list">
              <div className="conv-back" onClick={function() { setConvCategory(null); }}>{'\u2190'} {convCategory.name}</div>
              {convCategory.pairs.map(function(p) {
                return <button key={p.id} className="conv-item" onClick={function() {
                  if (lastAns !== null) {
                    try {
                      var converted = convertValue(lastAns, convCategory.id, p.id);
                      setLastAns(converted);
                      setDisplayResult(formatDecimal(converted));
                      setIsResultOnDisplay(true);
                    } catch { setDisplayResult('Argument ERROR'); setIsResultOnDisplay(true); }
                  }
                  setConvOpen(false);
                  setConvCategory(null);
                }}>{p.id}: {p.label}</button>;
              })}
            </div>
          )}
        </div>
      )}

      {/* ═══ CONSTANTS OVERLAY ═══ */}
      {constsOpen && (
        <div className="overlay-panel consts-panel">
          <div className="overlay-title">CONST ({constsPage + 1}/{Math.ceil(CONSTANTS.length / 10)})</div>
          <div className="consts-list">
            {CONSTANTS.slice(constsPage * 10, (constsPage + 1) * 10).map(function(c, i) {
              return (
                <button key={c.id} className="consts-item" onClick={function() {
                  var ff = field();
                  if (ff) ff.typedText(String(c.value));
                  setConstsOpen(false);
                }}>
                  <span className="consts-idx">{i}</span>
                  <span className="consts-sym">{c.symbol}</span>
                  <span className="consts-name">{c.name}</span>
                </button>
              );
            })}
          </div>
          <div className="consts-nav">
            <button onClick={function() { setConstsPage(function(p) { return Math.max(p - 1, 0); }); }}>{'\u25B2'}</button>
            <button onClick={function() { setConstsPage(function(p) { return Math.min(p + 1, Math.floor((CONSTANTS.length - 1) / 10)); }); }}>{'\u25BC'}</button>
          </div>
        </div>
      )}
    </div>
  );
}
